import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  ScrollArea,
  toast,
} from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { DollarSign, Loader2, User } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { DeliveryInput, PaymentInput } from "../../domain/types";
import {
  posKeys,
  useBranchTaxSettings,
  useDeliveryPartners,
  useFindOrCreateCustomerByPhone,
  useOpenShift,
  useServiceChargePresets,
} from "../api/queries";
import { useCart } from "../context/CartContext";
import { usePOSContext } from "../context/POSContext";
import { clearCustomerDisplayCartDiscount } from "../lib/customer-display-cart-discount";
import { clearCustomerDisplayCustomer } from "../lib/customer-display-customer";
import { useSyncCustomerDisplayCustomer } from "../hooks/useSyncCustomerDisplayCustomer";
import { buildLoyaltyPreviewItemsFromCart } from "../lib/loyalty-preview-items";
import { buildMemberReceiptSnapshotForLocalCompleteWithTimeout } from "../lib/member-receipt";
import { scheduleDeferredPrint, yieldToMain } from "../lib/print-html";
import { paymentUiLog } from "../lib/payment-ui-log";
import { requestAutoSyncAfterComplete } from "../lib/offline-sync/auto-sync";
import { completeLocalOrder, createLocalOrder } from "../lib/offline-sync/db";
import {
  buildLocalPricingSnapshot,
  buildOrderSummaryCouponSnapshot,
  type LocalPricingBaseTotals,
  mergeLocalPricingSnapshot,
} from "../lib/offline-sync/local-pricing-snapshot";
import { addProvisionalLoyaltyRedemption } from "../lib/offline-sync/loyalty-provisional";
import { resolveLocalFirstCustomerByPhone } from "../lib/offline-sync/resolve-local-first-customer";
import type { LocalOrderCustomerSnapshot } from "../lib/offline-sync/types";
import {
  printLocalProformaAndKitchen,
  printLocalReceiptAndKitchen,
} from "../lib/print-order-bill";
import { DeliveryForm } from "./DeliveryForm";
import { LoyaltyPointsPreview } from "./LoyaltyPointsPreview";
import {
  PaymentDialog,
  type PaymentDialogCompletePricing,
  type PaymentDialogCouponApplication,
  type PaymentDialogCustomerSelection,
  type PaymentDialogOrderItem,
} from "./PaymentDialog";

const CART_COUPON_ITEM_ID_PREFIX = "__cart_";

type LocalFirstCustomerSelection = PaymentDialogCustomerSelection;

function getCustomerSourceLabel(
  source?: LocalOrderCustomerSnapshot["source"],
): string {
  switch (source) {
    case "server":
      return "server customer";
    case "cache":
      return "cached customer";
    case "local_draft":
      return "local draft";
    default:
      return "customer";
  }
}

function parsePaymentDialogCouponItemIds(
  couponOrderItemIds: string[] | undefined,
  cartLength: number,
): {
  paymentCouponCartIndices?: number[];
  couponOrderItemIdsForApi?: string[];
} {
  if (!couponOrderItemIds?.length) {
    return {};
  }
  const allCartPrefixed = couponOrderItemIds.every((id) =>
    id.startsWith(CART_COUPON_ITEM_ID_PREFIX),
  );
  if (!allCartPrefixed) {
    return { couponOrderItemIdsForApi: couponOrderItemIds };
  }
  const indices = couponOrderItemIds.map((id) =>
    Number.parseInt(id.slice(CART_COUPON_ITEM_ID_PREFIX.length), 10),
  );
  if (
    indices.some(
      (n) =>
        Number.isNaN(n) || !Number.isInteger(n) || n < 0 || n >= cartLength,
    )
  ) {
    // ບໍ່ສົ່ງ id ບໍ່ຖືກໄປ API — ປ່ອຍໃຫ້ coupon ຈ່າຍເປັນລະດັບອໍເດີຖ້າມີໂຄດ
    return {};
  }
  return { paymentCouponCartIndices: indices };
}

function parsePaymentCouponApplications(
  applications: PaymentDialogCouponApplication[] | undefined,
  cartLength: number,
): {
  paymentCouponApplications?: Array<{
    code: string;
    cartIndices?: number[];
    orderItemIds?: string[];
  }>;
} {
  if (!applications?.length) {
    return {};
  }
  const out: Array<{
    code: string;
    cartIndices?: number[];
    orderItemIds?: string[];
  }> = [];
  for (const app of applications) {
    const ids = app.orderItemIds ?? [];
    if (ids.length === 0) continue;
    const allCart = ids.every((id) =>
      id.startsWith(CART_COUPON_ITEM_ID_PREFIX),
    );
    if (allCart) {
      const indices = ids.map((id) =>
        Number.parseInt(id.slice(CART_COUPON_ITEM_ID_PREFIX.length), 10),
      );
      if (
        indices.some(
          (n) =>
            Number.isNaN(n) || !Number.isInteger(n) || n < 0 || n >= cartLength,
        )
      ) {
        continue;
      }
      out.push({ code: app.code, cartIndices: indices });
    } else {
      out.push({ code: app.code, orderItemIds: ids });
    }
  }
  return out.length > 0 ? { paymentCouponApplications: out } : {};
}

type OrderSummaryDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

export function OrderSummaryDialog({
  open,
  onOpenChange,
  onSuccess,
}: OrderSummaryDialogProps) {
  const { items, total, clearCart } = useCart();
  const { selection, setSelection } = usePOSContext();
  const { data: openShift } = useOpenShift(
    selection?.branchId ?? null,
    selection?.deviceId ?? null,
  );
  const { data: deliveryPartners = [], isLoading: isLoadingPartners } =
    useDeliveryPartners(selection?.branchId ?? null);

  const { data: taxSettings } = useBranchTaxSettings(
    selection?.branchId ?? null,
  );

  const { data: serviceChargePresets = [] } = useServiceChargePresets(
    selection?.branchId ?? null,
  );
  const [isDelivery, setIsDelivery] = useState(false);
  const [deliveryData, setDeliveryData] = useState<DeliveryInput>({
    partnerId: "",
  });
  const [deliveryErrors, setDeliveryErrors] = useState<Record<string, string>>(
    {},
  );
  const [customerPhone, setCustomerPhone] = useState("");
  const [selectedCustomer, setSelectedCustomer] =
    useState<LocalFirstCustomerSelection | null>(null);
  const queryClient = useQueryClient();
  const findOrCreateCustomerMutation = useFindOrCreateCustomerByPhone();

  useSyncCustomerDisplayCustomer({
    enabled: open,
    branchId: selection?.branchId ?? null,
    customer: selectedCustomer,
    clearWhenDisabled: true,
  });

  const clearOrderSummaryCouponState = () => {
    setValidatedCoupon(null);
    setSelectedCouponCartIndices([]);
    clearCustomerDisplayCartDiscount();
    queryClient.removeQueries({ queryKey: ["pos", "coupons", "eligible"] });
  };
  // const [couponCode, setCouponCode] = useState("");
  const [validatedCoupon, setValidatedCoupon] = useState<{
    code: string;
    coupon: typeof import("@/server/platform/db/schema/pos-sales/coupon").coupon.$inferSelect;
    calculatedDiscount: number;
  } | null>(null);
  /** ดัชนี cart items ที่เลือกใช้ coupon (0-based); ว่าง = ใช้ทั้งออเดอร์ */
  const [selectedCouponCartIndices, setSelectedCouponCartIndices] = useState<
    number[]
  >([]);
  // const validateCouponMutation = useValidateCoupon();
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isQueueingOfflineOrder, setIsQueueingOfflineOrder] = useState(false);
  const [isCreatingLocalOrder, setIsCreatingLocalOrder] = useState(false);

  // Order type logic: DELIVERY if checked, else auto-detect from tableId
  const orderType = isDelivery
    ? "DELIVERY"
    : selection?.tableId
      ? "DINE_IN"
      : "TAKEAWAY";

  const orderTypeLabel =
    orderType === "DELIVERY"
      ? "ຈັດສົ່ງ"
      : orderType === "DINE_IN"
        ? "ກິນໃນຮ້ານ"
        : "ສັ່ງກັບບ້ານ";

  // Helper function to calculate tax for a single item
  const calculateItemTax = (
    lineSubtotal: number,
    taxRate: number,
    taxIncluded: boolean,
  ): number => {
    if (!taxIncluded || taxRate === 0) {
      return 0;
    }
    const tax = lineSubtotal * (taxRate / (100 + taxRate));
    return Math.round(tax * 100) / 100;
  };

  // Helper function to calculate price excluding tax
  const calculatePriceExcludingTax = (
    inclusivePrice: number,
    taxRate: number,
    taxIncluded: boolean,
  ): number => {
    if (!taxIncluded || taxRate === 0) {
      return inclusivePrice;
    }
    const priceExcludingTax = inclusivePrice / (1 + taxRate / 100);
    return Math.round(priceExcludingTax * 100) / 100;
  };

  // Calculate subtotal by excluding tax from each item
  const subtotal = useMemo(() => {
    if (!taxSettings?.taxIncluded) {
      return total; // ถ้า tax ไม่รวม ก็ใช้ total ตามเดิม
    }
    return items.reduce((sum, item) => {
      const priceExcludingTax = calculatePriceExcludingTax(
        item.totalPrice,
        taxSettings.taxRate,
        taxSettings.taxIncluded,
      );
      return sum + priceExcludingTax;
    }, 0);
  }, [items, taxSettings, total]);

  // Calculate tax for each item and sum them up
  const taxTotal = useMemo(() => {
    if (!taxSettings || !taxSettings.taxIncluded) {
      return 0;
    }
    if (taxSettings.taxRate === 0) {
      return 0;
    }

    // Calculate tax for each item and sum
    let totalTax = 0;
    for (const item of items) {
      const itemTax = calculateItemTax(
        item.totalPrice,
        taxSettings.taxRate,
        taxSettings.taxIncluded,
      );
      totalTax += itemTax;
    }

    return Math.round(totalTax * 100) / 100;
  }, [items, taxSettings]);

  // Calculate service charge
  const serviceChargeTotal = useMemo(() => {
    if (serviceChargePresets.length === 0) {
      return 0;
    }

    let total = 0;
    for (const preset of serviceChargePresets) {
      // Check if preset applies to this order type
      let shouldApply = true;

      if (preset.metadata && typeof preset.metadata === "object") {
        const metadata = preset.metadata as {
          orderTypes?: ("DINE_IN" | "TAKEAWAY" | "DELIVERY")[];
        };

        if (metadata.orderTypes && metadata.orderTypes.length > 0) {
          shouldApply = metadata.orderTypes.includes(orderType);
        }
      }

      if (!shouldApply) {
        continue;
      }

      let amount = 0;
      // Normalize calculationMode to lowercase for comparison
      const calculationMode = preset.calculationMode?.toLowerCase();

      if (calculationMode === "percentage") {
        if (preset.rate) {
          amount = subtotal * (Number(preset.rate) / 100);
        }
      } else if (calculationMode === "fixed") {
        amount = Number(preset.amount);
      }

      total += amount;
    }

    return Math.round(total * 100) / 100;
  }, [subtotal, serviceChargePresets, orderType]);

  const finalCouponCode = validatedCoupon?.code ?? null;

  const totalDiscount = useMemo(() => {
    return validatedCoupon?.calculatedDiscount ?? 0;
  }, [validatedCoupon]);

  // Calculate grand total: subtotal + serviceChargeTotal + taxTotal - totalDiscount
  const grandTotal = subtotal + serviceChargeTotal + taxTotal - totalDiscount;

  /** ລາຍການສຳລັບ PaymentDialog ເລືອກ coupon ຕໍ່ບັນທັດ (ຈ່າຍກ່ອນ) — id = __cart_{index} */
  const paymentDialogOrderItems = useMemo((): PaymentDialogOrderItem[] => {
    return items.map((item, index) => {
      const name =
        item.variantName != null && String(item.variantName).length > 0
          ? `${item.productName} (${item.variantName})`
          : item.productName;
      if (!taxSettings) {
        return {
          id: `${CART_COUPON_ITEM_ID_PREFIX}${index}`,
          productName: name,
          lineSubtotal: item.totalPrice,
          taxTotal: 0,
          variantId: item.variantId,
          quantity: item.quantity,
        };
      }
      const lineExcl = calculatePriceExcludingTax(
        item.totalPrice,
        taxSettings.taxRate,
        taxSettings.taxIncluded,
      );
      const lineTax = taxSettings.taxIncluded
        ? calculateItemTax(
            item.totalPrice,
            taxSettings.taxRate,
            taxSettings.taxIncluded,
          )
        : 0;
      return {
        id: `${CART_COUPON_ITEM_ID_PREFIX}${index}`,
        productName: name,
        lineSubtotal: lineExcl,
        taxTotal: lineTax,
        variantId: item.variantId,
        quantity: item.quantity,
      };
    });
  }, [items, taxSettings]);

  const loyaltyPreviewItems = useMemo(
    () =>
      buildLoyaltyPreviewItemsFromCart(
        items,
        paymentDialogOrderItems.map((row) => ({
          lineSubtotal: row.lineSubtotal,
          taxTotal: row.taxTotal,
        })),
      ),
    [items, paymentDialogOrderItems],
  );

  // Handle find or create customer button click
  const handleFindOrCreateCustomer = async () => {
    const trimmedPhone = customerPhone.trim();
    if (!trimmedPhone) {
      toast.error("ກະລຸນາປ້ອນເບີໂທລະສັບ");
      return;
    }
    if (!selection?.branchId) {
      toast.error("ກະລຸນາເລືອກສາຂາ");
      return;
    }

    const resolved = await resolveLocalFirstCustomerByPhone({
      branchId: selection.branchId,
      deviceId: selection.deviceId,
      phone: trimmedPhone,
      findOnline: () => findOrCreateCustomerMutation.mutateAsync(trimmedPhone),
      allowLocalDraft: true,
    });

    if (!resolved) {
      clearOrderSummaryCouponState();
      setSelectedCustomer(null);
      toast.error("ເກີດຂໍ້ຜິດພາດໃນການຄົ້ນຫາລູກຄ້າ");
      return;
    }

    clearOrderSummaryCouponState();
    setSelectedCustomer(resolved.selection);

    if (resolved.source === "server") {
      toast.success("ພົບລູກຄ້າສຳເລັດ");
      return;
    }
    if (resolved.source === "cache") {
      toast.success("ພົບລູກຄ້າຈາກ cache");
      return;
    }
    toast.success("ບັນທຶກລູກຄ້າໄວ້ໃນ local draft ແລ້ວ");
  };

  // Reset customer and coupon when dialog closes; clear stale display discount when opens
  useEffect(() => {
    if (!open) {
      setCustomerPhone("");
      setSelectedCustomer(null);
      clearOrderSummaryCouponState();
      clearCustomerDisplayCustomer();
      return;
    }
    clearCustomerDisplayCartDiscount();
  }, [open]);

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current && items.length > 0 && validatedCoupon) {
      setSelectedCouponCartIndices(items.map((_, i) => i));
    }
    prevOpenRef.current = open;
  }, [open, items, validatedCoupon]);

  // ຜູກລູກຄ້າໃໝ່ — ລ້າງສ່ວນຫຼຸດຄ້າງຈາກ auto-coupon ເກົ່າ / localStorage
  useEffect(() => {
    if (!open || !selectedCustomer) return;
    if (!validatedCoupon) {
      clearCustomerDisplayCartDiscount();
    }
  }, [open, selectedCustomer?.id, validatedCoupon]);

  const buildCustomerSnapshotForLocalOrder = (
    customer: LocalFirstCustomerSelection | null | undefined,
  ): LocalOrderCustomerSnapshot | undefined => {
    if (!customer) return undefined;
    if (customer.customerSnapshot) return customer.customerSnapshot;
    const capturedAt = customer.capturedAt ?? new Date().toISOString();
    return {
      customerId: customer.id || undefined,
      localCustomerId: customer.localCustomerId,
      phone: customer.phone ?? undefined,
      firstName: customer.firstName ?? undefined,
      lastName: customer.lastName ?? undefined,
      source: customer.source ?? "server",
      capturedAt,
    };
  };

  const getCustomerIdForLocalOrder = (
    customer: LocalFirstCustomerSelection | null | undefined,
  ) =>
    customer?.id ||
    customer?.customerSnapshot?.customerId ||
    undefined;

  const getCustomerSyncStatusForLocalOrder = (
    customer: LocalFirstCustomerSelection | null | undefined,
  ) => (customer?.source === "local_draft" ? "queued" : undefined);

  const getLocalPricingBaseTotals = (): LocalPricingBaseTotals => ({
    subtotal,
    taxTotal,
    serviceChargeTotal,
  });

  const buildOpenOrderPricingSnapshot = () => {
    const orderOpenCoupon =
      validatedCoupon && selectedCouponCartIndices.length > 0
        ? buildOrderSummaryCouponSnapshot({
            validatedCoupon,
            selectedCartIndices: selectedCouponCartIndices,
            cartItems: items,
          })
        : undefined;

    return buildLocalPricingSnapshot({
      base: getLocalPricingBaseTotals(),
      couponSnapshots: orderOpenCoupon ? [orderOpenCoupon] : [],
    });
  };

  const buildCheckoutPricingSnapshot = (
    paymentPricing?: PaymentDialogCompletePricing,
  ) => {
    const orderOpenCoupon =
      validatedCoupon && selectedCouponCartIndices.length > 0
        ? buildOrderSummaryCouponSnapshot({
            validatedCoupon,
            selectedCartIndices: selectedCouponCartIndices,
            cartItems: items,
          })
        : undefined;

    return mergeLocalPricingSnapshot({
      base: getLocalPricingBaseTotals(),
      existingCouponSnapshots: orderOpenCoupon ? [orderOpenCoupon] : [],
      paymentCouponSnapshots: paymentPricing?.paymentCouponSnapshots,
      paymentVoucherSnapshots: paymentPricing?.paymentVoucherSnapshots,
      rewardSnapshot: paymentPricing?.rewardSnapshot,
    });
  };

  // Handle validate coupon button click
  // const handleValidateCoupon = async () => {
  //   const trimmedCode = couponCode.trim().toUpperCase();
  //   if (!trimmedCode) {
  //     toast.error("ກະລຸນາປ້ອນໂຄດຄູປອງ");
  //     return;
  //   }

  //   try {
  //     const result = await validateCouponMutation.mutateAsync({
  //       code: trimmedCode,
  //       customerId: selectedCustomer?.id,
  //       orderSubtotal: subtotal,
  //     });

  //     if (
  //       result.valid &&
  //       result.coupon &&
  //       result.calculatedDiscount !== undefined
  //     ) {
  //       setValidatedCoupon({
  //         code: trimmedCode,
  //         coupon: result.coupon,
  //         calculatedDiscount: result.calculatedDiscount,
  //       });
  //       toast.success("ຄູປອງຖືກຕ້ອງ");
  //     } else {
  //       setValidatedCoupon(null);
  //       toast.error(result.error || "ຄູປອງບໍ່ຖືກຕ້ອງຫຼືໝົດອາຍຸ");
  //     }
  //   } catch (error) {
  //     console.error("Failed to validate coupon:", error);
  //     setValidatedCoupon(null);
  //     toast.error(
  //       error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການກວດສອບຄູປອງ",
  //     );
  //   }
  // };

  const validateDelivery = (): boolean => {
    if (!isDelivery) return true;

    const errors: Record<string, string> = {};

    if (!deliveryData.partnerId?.trim()) {
      errors.partnerId = "ກະລຸນາເລືອກຄູ່ຄ້າຈັດສົ່ງ";
    }

    setDeliveryErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleConfirm = async () => {
    if (!selection) {
      toast.error("ກະລຸນາເລືອກສາຂາແລະເຄື່ອງ POS");
      return;
    }

    if (items.length === 0) {
      toast.error("ກະຕ່າຫວ່າງ");
      return;
    }

    if (!validateDelivery()) {
      toast.error("ກະລຸນາຕື່ມຂໍ້ມູນການຈັດສົ່ງໃຫ້ຄົບຖ້ວນ");
      return;
    }

    try {
      const couponCodeToSend =
        finalCouponCode && selectedCouponCartIndices.length > 0
          ? finalCouponCode
          : undefined;
      const couponApplyToCartIndicesToSend =
        couponCodeToSend && selectedCouponCartIndices.length < items.length
          ? selectedCouponCartIndices
          : undefined;

      if (!selection.deviceId) {
        toast.error("ບໍ່ພົບ Device ID");
        return;
      }
      if (!openShift?.id) {
        toast.error("ບໍ່ພົບກະທີ່ເປີດຢູ່ ບໍ່ສາມາດບັນທຶກ local order ໄດ້");
        return;
      }

      setIsCreatingLocalOrder(true);
      const customerSnapshot =
        buildCustomerSnapshotForLocalOrder(selectedCustomer);
      const pricingSnapshot = buildOpenOrderPricingSnapshot();
      const localOrder = await createLocalOrder({
        branchId: selection.branchId,
        deviceId: selection.deviceId,
        shiftId: openShift.id,
        cartItems: items,
        selection,
        isDelivery,
        deliveryData: isDelivery ? deliveryData : undefined,
        customerId: getCustomerIdForLocalOrder(selectedCustomer),
        customerSnapshot,
        customerSyncStatus:
          getCustomerSyncStatusForLocalOrder(selectedCustomer),
        couponCode: couponCodeToSend,
        couponApplyToCartIndices: couponApplyToCartIndicesToSend,
        couponSnapshots: pricingSnapshot.couponSnapshots,
        discountBreakdown: pricingSnapshot.discountBreakdown,
        pricingSource: pricingSnapshot.pricingSource,
        totals: pricingSnapshot.totals,
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: posKeys.ordersListQueriesForBranch(selection.branchId),
        }),
        queryClient.invalidateQueries({
          queryKey: posKeys.branchTables(selection.branchId),
        }),
      ]);

      try {
        await printLocalProformaAndKitchen(localOrder);
      } catch (printErr) {
        toast.error(
          printErr instanceof Error ? printErr.message : "ເກີດຂໍ້ຜິດພາດໃນການພິມ",
        );
      }

      clearCart();
      // Clear table selection
      if (selection) {
        setSelection({
          ...selection,
          tableId: undefined,
          tableName: undefined,
        });
      }
      // Reset delivery form
      setDeliveryData({
        partnerId: "",
      });
      setDeliveryErrors({});
      setIsDelivery(false);
      setCustomerPhone("");
      setSelectedCustomer(null);
      toast.success("ສ້າງ Order ສຳເລັດແລ້ວ");
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການສ້າງ Order",
      );
    } finally {
      setIsCreatingLocalOrder(false);
    }
  };

  const handlePaymentComplete = async (
    payments: PaymentInput[],
    voucherCodes?: string[],
    paymentCouponCode?: string,
    couponOrderItemIds?: string[],
    paymentCouponApplicationsFromDialog?: PaymentDialogCouponApplication[],
    paymentCustomerId?: string,
    paymentCustomerSelection?: PaymentDialogCustomerSelection,
    _rewardId?: string,
    paymentPricing?: PaymentDialogCompletePricing,
  ) => {
    if (!selection?.branchId) {
      toast.error("ບໍ່ພົບ Branch ID");
      return;
    }

    const couponCodeToSend =
      finalCouponCode && selectedCouponCartIndices.length > 0
        ? finalCouponCode
        : undefined;
    const couponApplyToCartIndicesToSend =
      couponCodeToSend && selectedCouponCartIndices.length < items.length
        ? selectedCouponCartIndices
        : undefined;

    const multiCoupon = parsePaymentCouponApplications(
      paymentCouponApplicationsFromDialog,
      items.length,
    );
    const { paymentCouponCartIndices, couponOrderItemIdsForApi } =
      parsePaymentDialogCouponItemIds(couponOrderItemIds, items.length);

    try {
      if (!selection.deviceId) {
        toast.error("ບໍ່ພົບ Device ID");
        return;
      }
      if (!openShift?.id) {
        toast.error("ບໍ່ພົບກະທີ່ເປີດຢູ່ ບໍ່ສາມາດບັນທຶກ local order ໄດ້");
        return;
      }

      setIsQueueingOfflineOrder(true);
      setIsPaymentDialogOpen(false);
      await yieldToMain();
      paymentUiLog.info("local checkout start", {
        branchId: selection.branchId,
        deviceId: selection.deviceId,
        shiftId: openShift.id,
        itemCount: items.length,
        paymentCount: payments.length,
      });
      const effectiveCustomer = paymentCustomerSelection ?? selectedCustomer;
      const customerSnapshot =
        buildCustomerSnapshotForLocalOrder(effectiveCustomer);
      const pricingSnapshot = buildCheckoutPricingSnapshot(paymentPricing);
      const localOrder = await createLocalOrder({
        branchId: selection.branchId,
        deviceId: selection.deviceId,
        shiftId: openShift.id,
        cartItems: items,
        selection,
        isDelivery,
        deliveryData: isDelivery ? deliveryData : undefined,
        customerId:
          paymentCustomerId || getCustomerIdForLocalOrder(effectiveCustomer),
        customerSnapshot,
        customerSyncStatus:
          getCustomerSyncStatusForLocalOrder(effectiveCustomer),
        couponCode: couponCodeToSend,
        couponApplyToCartIndices: couponApplyToCartIndicesToSend,
        couponSnapshots: pricingSnapshot.couponSnapshots,
        voucherSnapshots: pricingSnapshot.voucherSnapshots,
        rewardSnapshot: pricingSnapshot.rewardSnapshot,
        rewardId:
          paymentPricing?.rewardId ?? pricingSnapshot.rewardSnapshot?.rewardId,
        discountBreakdown: pricingSnapshot.discountBreakdown,
        pricingSource: pricingSnapshot.pricingSource,
        totals: pricingSnapshot.totals,
      });
      const effectiveCustomerIdForReward =
        paymentCustomerId || getCustomerIdForLocalOrder(effectiveCustomer);
      await yieldToMain();
      const memberReceiptSnapshot =
        await buildMemberReceiptSnapshotForLocalCompleteWithTimeout({
          branchId: selection.branchId,
          customerId: effectiveCustomerIdForReward,
          customerSnapshot,
          cartItems: items,
          loyaltyPointsRedeemed:
            pricingSnapshot.rewardSnapshot?.pointsCost,
          rewardSnapshot: pricingSnapshot.rewardSnapshot,
        });
      const paidOrder = await completeLocalOrder(localOrder.clientOrderId, {
        payments,
        completedShiftId: openShift?.id,
        customerId:
          paymentCustomerId || getCustomerIdForLocalOrder(effectiveCustomer),
        customerSnapshot,
        customerSyncStatus:
          getCustomerSyncStatusForLocalOrder(effectiveCustomer),
        memberReceiptSnapshot,
        voucherCodes,
        couponSnapshots: pricingSnapshot.couponSnapshots,
        voucherSnapshots: pricingSnapshot.voucherSnapshots,
        rewardSnapshot: pricingSnapshot.rewardSnapshot,
        rewardId:
          paymentPricing?.rewardId ?? pricingSnapshot.rewardSnapshot?.rewardId,
        discountBreakdown: pricingSnapshot.discountBreakdown,
        pricingSource: pricingSnapshot.pricingSource,
        totals: pricingSnapshot.totals,
        ...(multiCoupon.paymentCouponApplications
          ? { paymentCouponApplications: multiCoupon.paymentCouponApplications }
          : {
              paymentCouponCode,
              couponOrderItemIds: couponOrderItemIdsForApi,
              paymentCouponCartIndices,
            }),
      });
      if (
        pricingSnapshot.rewardSnapshot &&
        effectiveCustomerIdForReward &&
        pricingSnapshot.rewardSnapshot.pointsCost > 0
      ) {
        addProvisionalLoyaltyRedemption({
          branchId: selection.branchId,
          customerId: effectiveCustomerIdForReward,
          clientOrderId: localOrder.clientOrderId,
          points: pricingSnapshot.rewardSnapshot.pointsCost,
        });
      }
      const completedOrder = paidOrder ?? localOrder;

      clearCart();
      if (selection) {
        setSelection({
          ...selection,
          tableId: undefined,
          tableName: undefined,
        });
      }
      setDeliveryData({
        partnerId: "",
      });
      setDeliveryErrors({});
      setIsDelivery(false);
      setCustomerPhone("");
      setSelectedCustomer(null);
      setIsQueueingOfflineOrder(false);
      toast.success("ຈ່າຍເງິນສຳເລັດແລ້ວ");
      onSuccess?.();
      onOpenChange(false);
      paymentUiLog.info("local checkout success", {
        clientOrderId: completedOrder.clientOrderId,
        clientOrderNo: completedOrder.clientOrderNo,
      });

      const syncScope = {
        branchId: selection.branchId,
        deviceId: selection.deviceId,
        shiftId: openShift.id,
      };
      void (async () => {
        await yieldToMain();
        requestAutoSyncAfterComplete(syncScope);
        try {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: posKeys.ordersListQueriesForBranch(selection.branchId),
            }),
            queryClient.invalidateQueries({
              queryKey: posKeys.branchTables(selection.branchId),
            }),
          ]);
        } catch (invalidateErr) {
          paymentUiLog.error("local checkout invalidate failed", {
            errorMessage:
              invalidateErr instanceof Error
                ? invalidateErr.message
                : String(invalidateErr),
          });
        }
      })();

      scheduleDeferredPrint(() => {
        return printLocalReceiptAndKitchen(completedOrder).catch((printErr) => {
          paymentUiLog.error("local print failed", {
            clientOrderId: completedOrder.clientOrderId,
            errorMessage:
              printErr instanceof Error ? printErr.message : String(printErr),
          });
          toast.error(
            printErr instanceof Error ? printErr.message : "ເກີດຂໍ້ຜິດພາດໃນການພິມ",
          );
        });
      });
    } catch (error) {
      paymentUiLog.error("local checkout failed", {
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການຈ່າຍເງິນ",
      );
    } finally {
      setIsQueueingOfflineOrder(false);
    }
  };

  const toggleCouponCartIndex = (index: number) => {
    setSelectedCouponCartIndices((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index].sort((a, b) => a - b),
    );
  };
  const selectAllCouponCartItems = () => {
    setSelectedCouponCartIndices(items.map((_, i) => i));
  };
  const clearAllCouponCartItems = () => {
    setSelectedCouponCartIndices([]);
  };

  const handleDeliveryChange = (checked: boolean) => {
    setIsDelivery(checked);
    if (!checked) {
      // Reset delivery data when unchecking
      setDeliveryData({
        partnerId: "",
      });
      setDeliveryErrors({});
    }
  };

  const handleConfirmAndPay = () => {
    if (!selection) {
      toast.error("ກະລຸນາເລືອກສາຂາແລະເຄື່ອງ POS");
      return;
    }

    if (items.length === 0) {
      toast.error("ກະຕ່າຫວ່າງ");
      return;
    }

    if (!validateDelivery()) {
      toast.error("ກະລຸນາຕື່ມຂໍ້ມູນການຈັດສົ່ງໃຫ້ຄົບຖ້ວນ");
      return;
    }

    // ເປີດ Payment ກ່ອນ — ສ້າງ order ຫຼັງຈ່າຍສຳເລັດ (checkout-and-complete)
    setIsPaymentDialogOpen(true);
  };

  const paymentInitialCustomer = useMemo(
    () =>
      selectedCustomer
        ? {
            id:
              selectedCustomer.id ||
              selectedCustomer.customerSnapshot?.customerId ||
              "",
            localCustomerId: selectedCustomer.localCustomerId,
            firstName: selectedCustomer.firstName,
            lastName: selectedCustomer.lastName,
            phone: selectedCustomer.phone,
            source: selectedCustomer.source,
            capturedAt: selectedCustomer.capturedAt,
          }
        : null,
    [
      selectedCustomer?.id,
      selectedCustomer?.localCustomerId,
      selectedCustomer?.firstName,
      selectedCustomer?.lastName,
      selectedCustomer?.phone,
      selectedCustomer?.source,
      selectedCustomer?.capturedAt,
      selectedCustomer?.customerSnapshot?.customerId,
    ],
  );

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-4xl!">
        <DialogHeader>
          <DialogTitle>ສະຫຼຸບ Order</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-6">
          {/* Left Column: Order Info, Delivery, Coupon, Customer */}
          <ScrollArea className="max-h-[80svh] pr-4">
            <div className="flex flex-col gap-4">
              {/* Order Info */}
              <div className="space-y-2 border-b pb-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="delivery"
                    checked={isDelivery}
                    onCheckedChange={(checked) =>
                      handleDeliveryChange(checked === true)
                    }
                  />
                  <Label htmlFor="delivery" className="cursor-pointer">
                    ຈັດສົ່ງ
                  </Label>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">ປະເພດ:</span>
                  <span className="font-medium">{orderTypeLabel}</span>
                </div>
                {!isDelivery && selection?.tableId && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">ໂຕະ:</span>
                    <span className="font-medium">{selection.tableName}</span>
                  </div>
                )}
                {selection?.salespersonName && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">
                      ຄົນຂາຍ:
                    </span>
                    <span className="font-medium">
                      {selection.salespersonName}
                    </span>
                  </div>
                )}
              </div>

              {/* Delivery Form */}
              {isDelivery && (
                <DeliveryForm
                  value={deliveryData}
                  onChange={setDeliveryData}
                  errors={deliveryErrors}
                  branchId={selection?.branchId ?? null}
                  partners={deliveryPartners}
                  isLoadingPartners={isLoadingPartners}
                />
              )}

              {/* Coupon Section */}
              {/* <div className="space-y-2 border-b pb-4">
                <Label htmlFor="coupon-code" className="text-sm">
                  ໂຄດຄູປອງ (ບໍ່ບັງຄັບ)
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="coupon-code"
                    type="text"
                    placeholder="ປ້ອນໂຄດຄູປອງ"
                    value={couponCode}
                    onChange={(e) =>
                      setCouponCode(e.target.value.toUpperCase())
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleValidateCoupon();
                      }
                    }}
                    disabled={validateCouponMutation.isPending}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleValidateCoupon}
                    disabled={
                      validateCouponMutation.isPending || !couponCode.trim()
                    }
                  >
                    {validateCouponMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "ກວດສອບ"
                    )}
                  </Button>
                </div>
                {(validatedCoupon || autoSelectedCoupon) && (
                  <div className="flex items-center justify-between rounded-md bg-muted p-2">
                    <div className="flex flex-col gap-1">
                      <span className="font-medium text-sm">
                        {validatedCoupon?.code ?? autoSelectedCoupon?.code}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        ສ່ວນຫຼຸດ:{" "}
                        {(
                          validatedCoupon?.calculatedDiscount ??
                          autoSelectedCoupon?.calculatedDiscount ??
                          0
                        ).toLocaleString("lo-LA")}{" "}
                        ₭
                      </span>
                    </div>
                    {validatedCoupon && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCouponCode("");
                          setValidatedCoupon(null);
                        }}
                      >
                        ລຶບ
                      </Button>
                    )}
                  </div>
                )}
              </div> */}

              {/* Customer Section */}
              <div className="space-y-2 border-b pb-4">
                <Label htmlFor="customer-phone" className="text-sm">
                  ເບີໂທລະສັບລູກຄ້າ (ບໍ່ບັງຄັບ)
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="customer-phone"
                    type="tel"
                    placeholder="ປ້ອນເບີໂທລະສັບ"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        void handleFindOrCreateCustomer();
                      }
                    }}
                    disabled={findOrCreateCustomerMutation.isPending}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleFindOrCreateCustomer}
                    disabled={
                      findOrCreateCustomerMutation.isPending ||
                      !customerPhone.trim()
                    }
                    variant="outline"
                  >
                    {findOrCreateCustomerMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ກຳລັງຄົ້ນຫາ...
                      </>
                    ) : (
                      "ຄົ້ນຫາ"
                    )}
                  </Button>
                </div>
                {selectedCustomer && (
                  <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/50 p-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground text-sm">
                      ລູກຄ້າ: {selectedCustomer.firstName}{" "}
                      {selectedCustomer.lastName} ({selectedCustomer.phone})
                    </span>
                    <Badge variant="outline" className="text-[11px]">
                      {getCustomerSourceLabel(selectedCustomer.source)}
                    </Badge>
                  </div>
                )}
                <LoyaltyPointsPreview
                  customerId={selectedCustomer?.id}
                  items={loyaltyPreviewItems}
                  enabled={open}
                />
              </div>
            </div>
          </ScrollArea>

          {/* Right Column: Items List and Pricing Summary */}
          <div className="flex flex-col">
            <ScrollArea className="max-h-[60svh] flex-1 pr-4">
              {validatedCoupon && items.length > 0 && (
                <div className="mb-3 space-y-2 border-b pb-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-muted-foreground text-xs">
                      ໃຊ້ coupon ກັບລາຍການ:
                    </span>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={selectAllCouponCartItems}
                      >
                        ທັງໝົດ
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={clearAllCouponCartItems}
                      >
                        ຍົກເລີກ
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {/* Items List */}
              <div className="space-y-3">
                {items.map((item, index) => {
                  // item.totalPrice is inclusive of tax
                  const totalPriceInclusive = item.totalPrice;

                  // Calculate price excluding tax for display
                  const totalPriceExclusive = taxSettings?.taxIncluded
                    ? calculatePriceExcludingTax(
                        totalPriceInclusive,
                        taxSettings.taxRate,
                        taxSettings.taxIncluded,
                      )
                    : totalPriceInclusive;

                  // Calculate unit price excluding tax
                  const unitPriceExclusive =
                    totalPriceExclusive / item.quantity;

                  return (
                    <div
                      key={item.id}
                      className="flex flex-col gap-2 border-b pb-3 last:border-b-0"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <h4 className="font-medium text-sm">
                            {item.productName}
                          </h4>
                          {item.variantName && (
                            <p className="text-muted-foreground text-xs">
                              {item.variantName}
                            </p>
                          )}
                          {item.modifiers.length > 0 && (
                            <div className="mt-1 space-y-0.5">
                              {item.modifiers.map((modifier) => (
                                <p
                                  key={modifier.modifierId}
                                  className="text-muted-foreground text-xs"
                                >
                                  + {modifier.modifierName}:{" "}
                                  {modifier.optionLabel}
                                  {modifier.priceDelta !== 0 && (
                                    <span className="ml-1">
                                      ({modifier.priceDelta > 0 ? "+" : ""}
                                      {modifier.priceDelta.toLocaleString(
                                        "lo-LA",
                                      )}{" "}
                                      ₭)
                                    </span>
                                  )}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {validatedCoupon && (
                        <label className="flex cursor-pointer items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={selectedCouponCartIndices.includes(index)}
                            onChange={() => toggleCouponCartIndex(index)}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <span className="text-muted-foreground text-xs">
                            ໃຊ້ສ່ວນຫຼຸດກັບລາຍການນີ້
                          </span>
                        </label>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground text-sm">
                              ຈຳນວນ: {item.quantity}
                            </span>
                            {item.quantity > 1 && (
                              <span className="text-muted-foreground text-xs">
                                ({unitPriceExclusive.toLocaleString("lo-LA")} ₭
                                x {item.quantity})
                              </span>
                            )}
                          </div>
                          {(() => {
                            const itemTax = taxSettings?.taxIncluded
                              ? calculateItemTax(
                                  totalPriceInclusive,
                                  taxSettings.taxRate,
                                  taxSettings.taxIncluded,
                                )
                              : 0;
                            return itemTax > 0 ? (
                              <p className="text-muted-foreground text-xs">
                                ພາສີ: {itemTax.toLocaleString("lo-LA")} ₭
                              </p>
                            ) : null;
                          })()}
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-sm">
                            {totalPriceExclusive.toLocaleString("lo-LA")} ₭
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            {/* Pricing Summary - Sticky at bottom */}
            <div className="mt-4 border-t pt-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">
                    ລາຄາຍ່ອຍ:
                  </span>
                  <span className="font-medium">
                    {subtotal.toLocaleString("lo-LA")} ₭
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">ພາສີ:</span>
                  <span className="font-medium">
                    {taxTotal.toLocaleString("lo-LA")} ₭
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">
                    ຄ່າບໍລິການ:
                  </span>
                  <span className="font-medium">
                    {serviceChargeTotal.toLocaleString("lo-LA")} ₭
                  </span>
                </div>
                {totalDiscount > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">
                      ສ່ວນຫຼຸດ:
                    </span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      -{totalDiscount.toLocaleString("lo-LA")} ₭
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between border-t pt-2">
                  <span className="font-semibold text-base">ລາຄາລວມ:</span>
                  <span className="font-bold text-lg text-primary">
                    {grandTotal.toLocaleString("lo-LA")} ₭
                  </span>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleConfirmAndPay}
                  disabled={
                    isCreatingLocalOrder ||
                    isQueueingOfflineOrder ||
                    items.length === 0
                  }
                  className="flex-1"
                >
                  <DollarSign className="mr-2 h-4 w-4" />
                  ຈ່າຍເງິນ
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleConfirm}
                  disabled={isCreatingLocalOrder || items.length === 0}
                >
                  {isCreatingLocalOrder ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4" />
                      ກຳລັງສ້າງ...
                    </>
                  ) : (
                    "ຢືນຢັນ Order"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

      {selection && (
        <PaymentDialog
          open={isPaymentDialogOpen}
          onOpenChange={(open) => {
            setIsPaymentDialogOpen(open);
            if (!open) {
              clearCustomerDisplayCartDiscount();
            }
          }}
          orderTotal={grandTotal}
          onComplete={handlePaymentComplete}
          isSubmitting={isQueueingOfflineOrder}
          branchId={selection.branchId}
          customerId={getCustomerIdForLocalOrder(selectedCustomer)}
          allowLocalCustomerDraft
          deviceId={selection.deviceId}
          initialCustomer={paymentInitialCustomer}
          orderItems={paymentDialogOrderItems}
          orderBaseInclTax={subtotal + taxTotal}
        />
      )}
    </>
  );
}
